import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'

import useAssetsMethodSelectionFlow from '@/components/core-tokens-list/hooks/use-assets-method-selection-flow'
import TokenDetailDialog from '@/components/core-tokens-list/TokenDetailDialog'
import { useBalances } from '@/hooks/use-balances'
import { BLACK_LIST_TOKENS_SYMBOLS } from '@/hooks/use-deposit-token-options'
import useMutableSearchParams from '@/hooks/use-mutable-search-params'
import { useFetchTopCoinList } from '@/hooks/use-nemo-apis'
import useTokens from '@/hooks/use-tokens'
import useTrans from '@/hooks/useTrans'
import type { APITokenInfo } from '@/utils/apis/helper'

import {
  ACTION_TYPE,
  COLUMNS_KEY,
  getTradeRoute,
  mapDataTopCoinToken,
  TOP_COINS_TAB,
} from './helpers'
import type { TopCoinsDataItem, ViewProps, ViewPropsCommon } from './types'
import View from './view'

const Container = () => {
  const router = useRouter()
  const trans = useTrans()
  const theme = useTheme()
  const { balances } = useBalances()
  const { tokenListMap } = useTokens()
  const { params, replaceState } = useMutableSearchParams()
  const [currentTab, setCurrentTab] = useState(TOP_COINS_TAB.spot)
  const [searchName, setSearchName] = useState('')
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const [selectedTokenInfo, setSelectedTokenInfo] = useState<APITokenInfo | undefined>()

  const { data: topCoins = [], isLoading } = useFetchTopCoinList(undefined)

  const {
    openDepositModal,
    openWithdrawModal,
    rendered: methodSelectionRendered,
  } = useAssetsMethodSelectionFlow()

  const onChangeTab = useCallback(
    (newTab: string) => {
      setCurrentTab(newTab)
      setSearchName('')
      replaceState({ tab: newTab }, true)
    },
    [replaceState],
  )

  useEffect(() => {
    if (params.tab && params.tab in TOP_COINS_TAB) {
      setCurrentTab(TOP_COINS_TAB[params.tab as keyof typeof TOP_COINS_TAB])
    }
  }, [params.tab])

  const topCoinsSearched = useMemo(() => {
    const searchNameFormatted = searchName.trim().toLowerCase()
    if (!searchNameFormatted) return topCoins
    return topCoins.filter((token) => {
      const name = token.token_name.toLowerCase()
      const symbol = token.token_symbol.toLowerCase()
      return name.includes(searchNameFormatted) || symbol.includes(searchNameFormatted)
    })
  }, [topCoins, searchName])

  const topCoinsData = useMemo(() => {
    return topCoinsSearched
      .map((token) => {
        return mapDataTopCoinToken(token, balances[token.token_symbol])
      })
      .filter(token => !BLACK_LIST_TOKENS_SYMBOLS.includes(token.symbol))
  }, [topCoinsSearched, balances])

  const onClickAction = useCallback(
    (data: TopCoinsDataItem, type: string) => {
      if (type === ACTION_TYPE.trade) {
        router.push(getTradeRoute(data.symbol))
        return
      }

      const tokenInfo = tokenListMap.get(data.tokenMint)
      if (tokenInfo && type === ACTION_TYPE.deposit) {
        openDepositModal(tokenInfo)
      }
      if (tokenInfo && type === ACTION_TYPE.withdraw) {
        openWithdrawModal(tokenInfo)
      }
    },
    [tokenListMap, router, openDepositModal, openWithdrawModal],
  )

  const columns = useMemo(() => {
    return [
      {
        key: COLUMNS_KEY.index,
        label: '#',
      },
      {
        key: COLUMNS_KEY.tokenName,
        label: trans.core_tokens_list.table_headers.token_name,
      },
      {
        key: COLUMNS_KEY.balances,
        label: trans.core_tokens_list.table_headers.balance,
      },
      {
        key: COLUMNS_KEY.volume24h,
        label: trans.core_tokens_list.token_detail.volume_24h,
      },
      {
        key: COLUMNS_KEY.diff24Hours,
        label: '24h %',
      },
      {
        key: COLUMNS_KEY.chart,
        label: trans.core_tokens_list.last_7_days,
      },
      {
        key: COLUMNS_KEY.actionsSpot,
        label: '',
        onClick: onClickAction,
      },
    ]
      .flat()
      .filter(Boolean) as ViewPropsCommon['columns']
  }, [trans, onClickAction])

  const onClickTokenItem = useCallback((data: TopCoinsDataItem) => {
    setSelectedTokenInfo(
      topCoins.find((item) => item.coin_address === data.tokenMint),
    )
  }, [topCoins])

  const onHandleCloseTokenInfo = useCallback(() => {
    setSelectedTokenInfo(undefined)
  }, [])

  const computedProps: ViewProps = {
    isLoading,
    searchName,
    setSearchName,
    topCoinsData,
    columns,
    isDesktop,
    onChangeTab,
    onClickTokenItem,
    methodSelectionRendered,
  }

  return (
    <>
      <View {...computedProps} />
      {selectedTokenInfo &&
        <TokenDetailDialog
          token={selectedTokenInfo}
          handleClose={onHandleCloseTokenInfo}
        />
      }
    </>
  )
}

export default Container
